import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarDays, ChevronRight, List, Search, Truck, Warehouse, X, AlertTriangle } from "lucide-react";
import { get } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LoadingState, ErrorState } from "@/components/common/States";
import { fmtDate, todayISO } from "@/lib/format";
import { label } from "@/lib/labels";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PortfolioMap } from "@/components/map/PortfolioMap";
import { MapPreviewCard } from "@/components/map/MapPreviewCard";
import { Glass } from "@/components/map/MapChrome";
import { MapDatePicker } from "@/components/map/MapDatePicker";
import { readStoredLayer, storeLayer } from "@/components/map/mapLayers";
import { alertCount, projectTone, TONE_HEX } from "@/components/map/mapUtils";

const FILTERS = [
  { id: "all", label: "Alle" },
  { id: "project", label: "Prosjekter" },
  { id: "depot", label: "Depot" },
  { id: "attention", label: "Oppfølging" },
];

/** Left rail: searchable list of everything on the map, grouped by kind. Doubles as the mobile sheet. */
const LocationList = ({ data, q, setQ, filter, setFilter, selectedId, onSelect, showTransports, setShowTransports, isToday }) => {
  const groups = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const all = (data?.locations || []).filter((l) => {
      if (filter === "project" && l.kind !== "project_site") return false;
      if (filter === "depot" && l.kind !== "depot") return false;
      if (filter === "attention" && alertCount(l) === 0) return false;
      if (ql && !`${l.name} ${l.project?.customer_name || ""} ${l.municipality || ""} ${l.address || ""}`.toLowerCase().includes(ql)) return false;
      return true;
    });
    const depots = all.filter((l) => l.kind === "depot");
    const projects = all.filter((l) => l.kind === "project_site").sort((a, b) => alertCount(b) - alertCount(a) || (b.modules_total || 0) - (a.modules_total || 0));
    return { depots, projects, count: all.length };
  }, [data, q, filter]);

  const Row = ({ l }) => {
    const p = l.project;
    const alerts = alertCount(l);
    const active = selectedId === l.id;
    const color = p ? TONE_HEX[projectTone(p.status)] : "#1F2446";
    return (
      <li>
        <button
          type="button"
          onClick={() => onSelect(active ? null : l.id)}
          aria-pressed={active}
          className={cn(
            "group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors duration-150 focus-visible:outline-none",
            active ? "bg-card shadow-[0_1px_0_rgba(16,24,40,0.04),0_2px_8px_-2px_rgba(16,24,40,0.12)]" : "hover:bg-card/80 focus-visible:bg-card/80",
          )}
          data-testid={`kart-list-item-${l.id}`}
        >
          <span className="relative flex h-7 w-7 shrink-0 items-center justify-center">
            {l.kind === "depot" ? (
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-navy text-white">
                <Warehouse className="h-3.5 w-3.5" strokeWidth={1.75} />
              </span>
            ) : (
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-card text-[10.5px] font-semibold tabular" style={{ boxShadow: `inset 0 0 0 2px ${color}` }}>
                {l.modules_total || p?.planned_modules || ""}
              </span>
            )}
            {alerts > 0 && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-background" aria-hidden />}
          </span>
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block truncate text-[12.5px] font-medium">{l.kind === "depot" ? l.name : p?.name}</span>
            <span className="block truncate text-[11.5px] text-muted-foreground">
              {l.kind === "depot"
                ? `${l.modules_total} moduler · ${(l.by_status || {}).ledig || 0} ledige`
                : `${label(p?.status)} · ${l.modules_total} på tomt${p?.planned_modules ? ` av ${p.planned_modules}` : ""}${!isToday ? " (forventet)" : ""}`}
            </span>
          </span>
          <ChevronRight className={cn("h-4 w-4 shrink-0 text-muted-foreground/50 transition-transform duration-150", active && "translate-x-0.5 text-foreground")} />
        </button>
      </li>
    );
  };

  return (
    <div className="flex h-full flex-col" data-testid="kart-list">
      <div className="space-y-3 px-4 pt-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk prosjekt, kunde, sted…" className="h-8 bg-card pl-8 pr-7" data-testid="kart-search-input" />
          {q && (
            <button type="button" onClick={() => setQ("")} className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground" aria-label="Tøm søk">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Filter">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              onClick={() => setFilter(f.id)}
              className={cn(
                "h-7 rounded-md px-2.5 text-[12px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60",
                filter === f.id ? "bg-foreground text-background font-medium" : "text-foreground/75 hover:bg-card hover:text-foreground",
              )}
              data-testid={`kart-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 pt-2 scrollbar-thin">
        {groups.count === 0 ? (
          <div className="px-3 py-10 text-center text-[12.5px] text-muted-foreground">Ingen treff.</div>
        ) : (
          <>
            {groups.depots.length > 0 && (
              <>
                <div className="px-2 pb-1 pt-2 text-[11px] font-medium text-muted-foreground">Depot · {groups.depots.length}</div>
                <ul className="space-y-px">
                  {groups.depots.map((l) => (
                    <Row key={l.id} l={l} />
                  ))}
                </ul>
              </>
            )}
            {groups.projects.length > 0 && (
              <>
                <div className="px-2 pb-1 pt-3 text-[11px] font-medium text-muted-foreground">Prosjekttomter · {groups.projects.length}</div>
                <ul className="space-y-px">
                  {groups.projects.map((l) => (
                    <Row key={l.id} l={l} />
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-3 text-[12px] text-muted-foreground">
        <label className="inline-flex items-center gap-2">
          <Switch checked={showTransports} onCheckedChange={setShowTransports} disabled={!isToday} data-testid="kart-transports-switch" />
          <span className="inline-flex items-center gap-1">
            <Truck className="h-3.5 w-3.5" strokeWidth={1.75} /> Transport
          </span>
        </label>
        <span className="tabular">{data?.totals?.modules} moduler</span>
      </div>
    </div>
  );
};

export default function KartPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const today = todayISO();
  const date = params.get("dato") || today;
  const isToday = date === today;
  const [selectedId, setSelectedId] = useState(params.get("lokasjon") || null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [showTransports, setShowTransports] = useState(true);
  const [layer, setLayer] = useState(readStoredLayer);
  const [listOpen, setListOpen] = useState(false);
  const changeLayer = (id) => {
    setLayer(id);
    storeLayer(id);
  };
  const setDate = (iso) => {
    const next = new URLSearchParams(params);
    if (!iso || iso === today) next.delete("dato");
    else next.set("dato", iso);
    setParams(next, { replace: true });
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["map-overview", date],
    queryFn: () => get("/map/overview", { date: isToday ? undefined : date }),
    refetchInterval: isToday ? 120_000 : false,
    enabled: can("projects:read"),
  });

  // deep-link from the assistant: ?prosjekt=<id> → select that project's site once data is in
  useEffect(() => {
    const pid = params.get("prosjekt");
    if (!pid || !data) return;
    const loc = data.locations.find((l) => l.project?.id === pid);
    if (loc) setSelectedId(loc.id);
    const next = new URLSearchParams(params);
    next.delete("prosjekt");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // keep selection in the URL so the view is shareable
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (selectedId) next.set("lokasjon", selectedId);
    else next.delete("lokasjon");
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [selectedId]);

  const visibleIds = useMemo(() => {
    if (!data) return null;
    const ql = q.trim().toLowerCase();
    if (filter === "all" && !ql) return null;
    return new Set(
      data.locations
        .filter((l) => {
          if (filter === "project" && l.kind !== "project_site") return false;
          if (filter === "depot" && l.kind !== "depot") return false;
          if (filter === "attention" && alertCount(l) === 0) return false;
          if (ql && !`${l.name} ${l.project?.customer_name || ""} ${l.municipality || ""} ${l.address || ""}`.toLowerCase().includes(ql)) return false;
          return true;
        })
        .map((l) => l.id),
    );
  }, [data, q, filter]);

  if (!can("projects:read")) return <ErrorState error={{ message: "Kartet krever prosjekttilgang." }} />;
  if (isLoading) return <LoadingState className="py-32" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const selected = data.locations.find((l) => l.id === selectedId) || null;
  const listProps = {
    data,
    q,
    setQ,
    filter,
    setFilter,
    selectedId,
    onSelect: (id) => {
      setSelectedId(id);
      setListOpen(false);
    },
    showTransports,
    setShowTransports,
    isToday,
  };
  const dateLabel = isToday ? null : `Forventet ${fmtDate(date, "d. MMM yyyy")}`;

  return (
    <div className="flex h-[calc(100vh-3rem)] min-h-[520px] fade-up" data-testid="kart-page">
      <aside className="hidden w-[324px] shrink-0 flex-col bg-muted/40 dark:bg-muted/20 lg:flex" data-testid="kart-rail">
        <div className="px-4 pt-4">
          <h1 className="text-[17px] font-semibold leading-6 tracking-tight">Kart</h1>
          <p className="text-[12px] text-muted-foreground">{isToday ? "Kjent lokasjon nå · ikke sanntid" : `Forventet plassering ${fmtDate(date, "d. MMMM yyyy")}`}</p>
        </div>
        <LocationList {...listProps} />
      </aside>

      <div className="relative min-w-0 flex-1">
        <PortfolioMap
          data={data}
          visibleIds={visibleIds}
          selectedId={selectedId}
          onSelect={setSelectedId}
          layer={layer}
          onLayerChange={changeLayer}
          showTransports={showTransports && isToday}
          fitPadding={{
            top: 72,
            bottom: 48,
            left: 64,
            right: selected ? 420 : 72,
          }}
          maxZoom={11}
          legend={false}
          topLeft={
            <>
              <MapDatePicker date={date} today={today} onChange={setDate} testId="kart-date-button" />
              <Glass className="lg:hidden">
                <button type="button" onClick={() => setListOpen(true)} className="inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[12px] font-medium" data-testid="kart-open-list">
                  <List className="h-3.5 w-3.5" /> Liste
                </button>
              </Glass>
            </>
          }
        >
          {!isToday && (
            <div className="pointer-events-none absolute left-1/2 top-3 z-10 hidden -translate-x-1/2 sm:block">
              <Glass className="flex h-9 items-center gap-2 px-4 text-[12px]" testId="kart-date-banner">
                <CalendarDays className="h-3.5 w-3.5 text-primary" strokeWidth={1.75} />
                <span>
                  Forventet plassering <span className="font-medium">{fmtDate(date, "EEEE d. MMMM yyyy")}</span> · basert på planlagte allokeringer
                </span>
                <button type="button" onClick={() => setDate(null)} className="ml-1 rounded p-0.5 text-muted-foreground hover:text-foreground" aria-label="Tilbake til i dag" data-testid="kart-date-reset">
                  <X className="h-3.5 w-3.5" />
                </button>
              </Glass>
            </div>
          )}

          {/* Detail panel */}
          {selected && (
            <div className="pointer-events-none absolute inset-y-3 right-3 z-20 hidden w-[384px] lg:block">
              <div className="pointer-events-auto h-full overflow-hidden rounded-xl bg-card/95 shadow-pop backdrop-blur-md fade-up" data-testid="kart-detail-panel">
                <MapPreviewCard loc={selected} onClose={() => setSelectedId(null)} variant="panel" dateLabel={dateLabel} />
              </div>
            </div>
          )}
          {selected && (
            <div className="pointer-events-none absolute bottom-3 left-3 z-20 max-w-full lg:hidden">
              <MapPreviewCard loc={selected} onClose={() => setSelectedId(null)} dateLabel={dateLabel} />
            </div>
          )}
          {!selected && (
            <div className="pointer-events-none absolute bottom-3 left-3 z-10 hidden rounded-lg bg-card/80 px-2.5 py-1.5 text-[11.5px] text-muted-foreground backdrop-blur sm:block" data-testid="kart-hint">
              {data.locations.filter((l) => l.kind === "project_site").length} prosjekttomter · {data.locations.filter((l) => l.kind === "depot").length} depot
              {isToday && ` · ${data.transports.length} planlagte transporter · ${data.live.modules_in_transit} moduler på vei`}
              {data.live.conflicts > 0 && (
                <button
                  type="button"
                  onClick={() => navigate("/planlegging?view=module&conflicts=1")}
                  className="pointer-events-auto ml-2 inline-flex items-center gap-1 text-amber-700 hover:underline dark:text-amber-300"
                  data-testid="kart-conflicts-link"
                >
                  <AlertTriangle className="h-3 w-3" /> {data.live.conflicts} konflikter
                </button>
              )}
            </div>
          )}
        </PortfolioMap>
      </div>

      <Sheet open={listOpen} onOpenChange={setListOpen}>
        <SheetContent side="left" className="w-[320px] p-0">
          <SheetHeader className="px-4 pt-4">
            <SheetTitle>Kart</SheetTitle>
          </SheetHeader>
          <LocationList {...listProps} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
